import { askAboutAndReveal } from "../../../state/chatActions";
import type { Tc40Record, Tc40ReasonCode } from "../../../data/types";
import { fmtEur } from "./format";
import styles from "./TC40MatchHeatmap.module.css";

interface TC40MatchHeatmapProps {
  records: Tc40Record[];
  /** Number of weekly columns to show (most recent on the right). */
  weeks?: number;
  /** Anchor "now" date — defaults to 2026-04-24 (matches portfolio.now). */
  now?: string;
}

const REASONS: { code: Tc40ReasonCode; label: string }[] = [
  { code: "10.4", label: "10.4 · fraud (cnp)" },
  { code: "13.1", label: "13.1 · svc not provided" },
  { code: "13.2", label: "13.2 · cancelled recurring" },
  { code: "13.5", label: "13.5 · misrep." },
  { code: "13.6", label: "13.6 · credit not processed" },
  { code: "13.7", label: "13.7 · cancelled merchandise" },
];

function startOfWeek(iso: string): Date {
  const d = new Date(iso);
  d.setUTCHours(0, 0, 0, 0);
  const day = d.getUTCDay();
  const offset = day === 0 ? 6 : day - 1; // week starts Monday
  d.setUTCDate(d.getUTCDate() - offset);
  return d;
}

function weekKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function weekLabel(d: Date): string {
  const month = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"][
    d.getUTCMonth()
  ];
  return `${month} ${String(d.getUTCDate()).padStart(2, "0")}`;
}

interface Cell {
  total: number;
  suppressed: number;
  matched: number;
  amount: number;
}

export function TC40MatchHeatmap({
  records,
  weeks = 8,
  now = "2026-04-24",
}: TC40MatchHeatmapProps) {
  const anchor = startOfWeek(now);
  const columns: Date[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(anchor);
    d.setUTCDate(d.getUTCDate() - i * 7);
    columns.push(d);
  }
  const columnKeys = columns.map(weekKey);
  const firstWeek = columns[0].getTime();
  const lastWeek = columns[columns.length - 1].getTime() + 7 * 24 * 60 * 60 * 1000;

  // Bucket records into (reason, week) cells.
  const cells = new Map<string, Cell>();
  let maxTotal = 0;
  let totalInView = 0;
  let suppressedInView = 0;
  let suppressedAmountInView = 0;

  for (const r of records) {
    const t = new Date(r.date).getTime();
    if (isNaN(t) || t < firstWeek || t >= lastWeek) continue;
    const wk = weekKey(startOfWeek(r.date));
    const key = `${r.reasonCode}|${wk}`;
    const cell = cells.get(key) ?? { total: 0, suppressed: 0, matched: 0, amount: 0 };
    cell.total += 1;
    if (r.suppressed) cell.suppressed += 1;
    else if (r.matchedCE3) cell.matched += 1;
    cell.amount += r.amountEur;
    cells.set(key, cell);
    if (cell.total > maxTotal) maxTotal = cell.total;
    totalInView += 1;
    if (r.suppressed) {
      suppressedInView += 1;
      suppressedAmountInView += r.amountEur;
    }
  }

  const suppressionRate = totalInView > 0 ? suppressedInView / totalInView : 0;

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>ce 3.0 match map</span>
        <span className={styles.muted}>
          last {weeks} weeks · {(suppressionRate * 100).toFixed(0)}% suppression ·{" "}
          {fmtEur(suppressedAmountInView)} retained
        </span>
      </header>

      <div
        className={styles.matrix}
        style={{
          gridTemplateColumns: `minmax(170px, auto) repeat(${columns.length}, minmax(32px, 1fr))`,
        }}
      >
        <div className={styles.colSpacer} />
        {columns.map((d) => (
          <div key={weekKey(d)} className={styles.colHeader}>
            {weekLabel(d)}
          </div>
        ))}

        {REASONS.map((reason) => (
          <RowFragment
            key={reason.code}
            code={reason.code}
            label={reason.label}
            cells={columnKeys.map((wk) => cells.get(`${reason.code}|${wk}`))}
            columnKeys={columnKeys}
            maxTotal={maxTotal}
          />
        ))}
      </div>

      <footer className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.high}`} /> ≥ 60% suppressed
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.mid}`} /> some matched
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.low}`} /> no match
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.empty}`} /> no disputes
        </span>
        <span className={styles.legendItem}>intensity = dispute count</span>
      </footer>
    </div>
  );
}

function RowFragment({
  code,
  label,
  cells,
  columnKeys,
  maxTotal,
}: {
  code: string;
  label: string;
  cells: (Cell | undefined)[];
  columnKeys: string[];
  maxTotal: number;
}) {
  const rowTotal = cells.reduce((s, c) => s + (c?.total ?? 0), 0);
  const rowSuppressed = cells.reduce((s, c) => s + (c?.suppressed ?? 0), 0);
  const rowRate = rowTotal > 0 ? rowSuppressed / rowTotal : 0;
  return (
    <>
      <button
        type="button"
        className={styles.rowLabel}
        onClick={() =>
          askAboutAndReveal(
            `explain reason code ${code} (${label}). how does ce 3.0 handle it, what's our current ${rowTotal > 0 ? `${(rowRate * 100).toFixed(0)}% suppression rate` : "performance"}, and which merchants are the biggest source of these?`
          )
        }
      >
        <span className={styles.rowLabelText}>{label}</span>
        <span className={styles.rowStat}>
          {rowTotal > 0 ? `${(rowRate * 100).toFixed(0)}%` : "—"}
        </span>
      </button>
      {cells.map((c, i) => {
        if (!c || c.total === 0) {
          return <div key={columnKeys[i]} className={`${styles.cell} ${styles.empty}`} />;
        }
        const rate = c.suppressed / c.total;
        const toneClass =
          c.total === 0
            ? styles.empty
            : rate >= 0.6
              ? styles.high
              : c.matched + c.suppressed > 0
                ? styles.mid
                : styles.low;
        const densityPct = maxTotal > 0 ? Math.max(0.18, c.total / maxTotal) : 0.18;
        const week = columnKeys[i];
        return (
          <button
            type="button"
            key={week}
            className={`${styles.cell} ${toneClass}`}
            style={{ opacity: densityPct }}
            title={
              `${c.total} dispute${c.total === 1 ? "" : "s"} · ` +
              `${c.suppressed} suppressed · ${c.matched} matched · ` +
              `${fmtEur(c.amount)} — click to dig in`
            }
            onClick={() =>
              askAboutAndReveal(
                `dig into reason ${code} for the week of ${week}: ${c.total} dispute${c.total === 1 ? "" : "s"}, ${c.suppressed} suppressed, ${c.matched} matched without suppression, ${fmtEur(c.amount)} total. which merchants are in this bucket and why did/didn't ce 3.0 land?`
              )
            }
          >
            <span className={styles.cellCount}>{c.total}</span>
          </button>
        );
      })}
    </>
  );
}
